// components/MindmapFilters.js - Composant de filtres avec style vert cohérent
import { Filter, RefreshCw, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

const MindmapFilters = ({
    filterByYear,
    filterByCategory,
    filterByStatus,
    availableYears,
    availableCategories,
    availableStatuses,
    onFilterYearChange,
    onFilterCategoryChange,
    onFilterStatusChange,
    onReset,
    onRefresh,
    resultCount,
    isLoading
}) => {
    const [isOpenYear, setIsOpenYear] = useState(false);
    const [isOpenCategory, setIsOpenCategory] = useState(false);
    const [isOpenStatus, setIsOpenStatus] = useState(false);

    const yearRef = useRef(null);
    const categoryRef = useRef(null);
    const statusRef = useRef(null);

    // Options construites à partir des données réelles
    const yearOptions = [
        { value: 'all', label: 'Toutes les années' },
        ...availableYears.map(year => ({ value: year, label: year }))
    ];

    const categoryOptions = [
        { value: 'all', label: 'Toutes catégories' },
        ...availableCategories.map(category => ({ value: category, label: category }))
    ];

    const statusOptions = [
        { value: 'all', label: 'Tous les statuts' },
        ...availableStatuses.map(status => ({ value: status, label: status }))
    ];

    // Gestion des clics extérieurs pour fermer les dropdowns
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (yearRef.current && !yearRef.current.contains(event.target)) {
                setIsOpenYear(false);
            }
            if (categoryRef.current && !categoryRef.current.contains(event.target)) {
                setIsOpenCategory(false);
            }
            if (statusRef.current && !statusRef.current.contains(event.target)) {
                setIsOpenStatus(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Vérifier si des filtres sont actifs
    const hasActiveFilters = filterByYear !== 'all' ||
        filterByCategory !== 'all' ||
        filterByStatus !== 'all';

    return (
        <div className="mb-4 p-4 bg-white rounded-xl border border-gray-200 shadow-sm flex-shrink-0">
            {/* Filtres en ligne avec boutons d'action */}
            <div className="flex flex-wrap lg:flex-nowrap items-center gap-3">
                {/* Filtre par catégorie */}
                <div className="flex items-center gap-2 min-w-0">
                    <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Catégorie</label>
                    <div className="relative w-40" ref={categoryRef}>
                        <button
                            onClick={() => setIsOpenCategory(!isOpenCategory)}
                            disabled={isLoading}
                            className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-[#bed1d0]/20 focus:border-[#bed1d0] transition-all duration-200 cursor-pointer hover:border-[#7a9896] text-left flex items-center justify-between shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <span className="text-gray-900 truncate">
                                {categoryOptions.find(opt => opt.value === filterByCategory)?.label || 'Sélectionner'}
                            </span>
                            <svg
                                className={`w-4 h-4 text-gray-400 transition-transform duration-200 flex-shrink-0 ml-1 ${isOpenCategory ? 'rotate-180' : ''}`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>

                        {isOpenCategory && (
                            <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-30 max-h-64 overflow-hidden">
                                <div className="max-h-64 overflow-y-auto">
                                    {categoryOptions.map((option) => (
                                        <button
                                            key={option.value}
                                            onClick={() => {
                                                onFilterCategoryChange && onFilterCategoryChange(option.value);
                                                setIsOpenCategory(false);
                                            }}
                                            className={`w-full text-left px-4 py-2 text-sm transition-colors duration-150 ${filterByCategory === option.value
                                                ? 'bg-[#bed1d0]/20 text-gray-800 font-medium'
                                                : 'text-gray-700 hover:bg-[#7a9896]/10 hover:text-gray-700'
                                                }`}
                                        >
                                            <span className="truncate block">{option.label}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Filtre par statut */}
                <div className="flex items-center gap-2 min-w-0">
                    <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Statut</label>
                    <div className="relative w-36" ref={statusRef}>
                        <button
                            onClick={() => setIsOpenStatus(!isOpenStatus)}
                            disabled={isLoading}
                            className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-[#bed1d0]/20 focus:border-[#bed1d0] transition-all duration-200 cursor-pointer hover:border-[#7a9896] text-left flex items-center justify-between shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <span className="text-gray-900 truncate">
                                {statusOptions.find(opt => opt.value === filterByStatus)?.label || 'Sélectionner'}
                            </span>
                            <svg
                                className={`w-4 h-4 text-gray-400 transition-transform duration-200 flex-shrink-0 ml-1 ${isOpenStatus ? 'rotate-180' : ''}`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>

                        {isOpenStatus && (
                            <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-30 max-h-64 overflow-hidden">
                                <div className="max-h-64 overflow-y-auto">
                                    {statusOptions.map((option) => (
                                        <button
                                            key={option.value}
                                            onClick={() => {
                                                onFilterStatusChange && onFilterStatusChange(option.value);
                                                setIsOpenStatus(false);
                                            }}
                                            className={`w-full text-left px-4 py-2 text-sm transition-colors duration-150 ${filterByStatus === option.value
                                                ? 'bg-[#bed1d0]/20 text-gray-800 font-medium'
                                                : 'text-gray-700 hover:bg-[#7a9896]/10 hover:text-gray-700'
                                                }`}
                                        >
                                            <span className="truncate block">{option.label}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Filtre par année */}
                <div className="flex items-center gap-2 min-w-0">
                    <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Année</label>
                    <div className="relative w-36" ref={yearRef}>
                        <button
                            onClick={() => setIsOpenYear(!isOpenYear)}
                            disabled={isLoading}
                            className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-[#bed1d0]/20 focus:border-[#bed1d0] transition-all duration-200 cursor-pointer hover:border-[#7a9896] text-left flex items-center justify-between shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <span className="text-gray-900 truncate">
                                {yearOptions.find(opt => opt.value === filterByYear)?.label || 'Sélectionner'}
                            </span>
                            <svg
                                className={`w-4 h-4 text-gray-400 transition-transform duration-200 flex-shrink-0 ml-1 ${isOpenYear ? 'rotate-180' : ''}`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>

                        {isOpenYear && (
                            <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-30 max-h-64 overflow-hidden">
                                <div className="max-h-64 overflow-y-auto">
                                    {yearOptions.map((option) => (
                                        <button
                                            key={option.value}
                                            onClick={() => {
                                                onFilterYearChange && onFilterYearChange(option.value);
                                                setIsOpenYear(false);
                                            }}
                                            className={`w-full text-left px-4 py-2 text-sm transition-colors duration-150 ${filterByYear === option.value
                                                ? 'bg-[#bed1d0]/20 text-gray-800 font-medium'
                                                : 'text-gray-700 hover:bg-[#7a9896]/10 hover:text-gray-700'
                                                }`}
                                        >
                                            <span className="truncate block">{option.label}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Bouton Réinitialiser */}
                <div className="flex-shrink-0 ml-auto">
                    <button
                        onClick={onReset}
                        disabled={isLoading}
                        className="w-10 h-[42px] flex items-center justify-center text-gray-600 hover:text-gray-700 hover:bg-[#7a9896]/10 border border-gray-200 hover:border-[#7a9896] rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Réinitialiser les filtres"
                    >
                        <RefreshCw className="w-4 h-4" />
                    </button>
                </div>
            </div>

            {/* Résumé des filtres actifs */}
            {(hasActiveFilters || isLoading) && (
                <div className="mt-4 pt-3 border-t border-gray-100">
                    <div className="flex items-center justify-between">
                        {/* Indicateur de chargement */}
                        {isLoading && (
                            <div className="flex items-center gap-2 text-xs text-gray-500">
                                <RefreshCw className="w-3 h-3 animate-spin" />
                                Actualisation...
                            </div>
                        )}

                        {/* Filtres actifs sous forme de badges */}
                        {hasActiveFilters && !isLoading && (
                            <div className="flex flex-wrap gap-1">
                                {filterByCategory !== 'all' && (
                                    <span className="inline-flex items-center px-2 py-1 text-xs bg-[#7a9896]/20 text-gray-700 rounded-full">
                                        {filterByCategory}
                                        <button
                                            onClick={() => onFilterCategoryChange && onFilterCategoryChange('all')}
                                            className="ml-1 hover:text-gray-600"
                                        >
                                            <X className="w-3 h-3" />
                                        </button>
                                    </span>
                                )}

                                {filterByStatus !== 'all' && (
                                    <span className="inline-flex items-center px-2 py-1 text-xs bg-[#7a9896]/20 text-gray-700 rounded-full">
                                        {statusOptions.find(opt => opt.value === filterByStatus)?.label}
                                        <button
                                            onClick={() => onFilterStatusChange && onFilterStatusChange('all')}
                                            className="ml-1 hover:text-gray-600"
                                        >
                                            <X className="w-3 h-3" />
                                        </button>
                                    </span>
                                )}

                                {filterByYear !== 'all' && (
                                    <span className="inline-flex items-center px-2 py-1 text-xs bg-[#7a9896]/20 text-gray-700 rounded-full">
                                        {filterByYear}
                                        <button
                                            onClick={() => onFilterYearChange && onFilterYearChange('all')}
                                            className="ml-1 hover:text-gray-600"
                                        >
                                            <X className="w-3 h-3" />
                                        </button>
                                    </span>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default MindmapFilters;